import PageHero from "@/components/PageHero";
import ImageFrame from "@/components/ImageFrame";
import CTABand from "@/components/CTABand";
import { Reveal, MaskedLines, ChapterLabel } from "@/components/motion";
import { IMAGES } from "@/data/content";

const STEPS = [
  {
    title: "Indledende samtale & spørgeskema",
    text: "Forløbet starter med en indledende samtale om det, du eller dit barn oplever, hverdagen og de områder, du gerne vil arbejde med. Inden træningen begynder, udfyldes et spørgeskema, som giver et struktureret udgangspunkt for forløbet.",
  },
  {
    title: "Individuelt tilrettelagt forløb",
    text: "Ikke to hjerner er ens. Derfor skræddersys hvert forløb individuelt og justeres løbende ud fra respons, symptomer og den udvikling, der viser sig undervejs.",
  },
  {
    title: "Træningssessioner",
    text: "En session varer ca. 40 minutter og indeholder typisk omkring 30 minutters neurofeedbacktræning. De fleste træner 1 til 2 gange om ugen i rolige rammer.",
  },
  {
    title: "Evaluering efter 10 træninger",
    text: "Efter 10 træninger udfyldes spørgeskemaet igen. Jeg sammenligner besvarelserne med udgangspunktet og ser systematisk på udviklingen i de områder, der er relevante for netop dit forløb.",
  },
  {
    title: "Evaluering efter 20 træninger",
    text: "Efter 20 træninger gentages spørgeskemaet. Jeg gennemgår udviklingen sammen med dig og vurderer individuelt, om yderligere træning giver mening.",
  },
];

const FACTS = [
  ["Session", "Ca. 40 minutter, heraf typisk omkring 30 minutters neurofeedbacktræning"],
  ["Forløb", "Minimum 20 træninger, individuelt tilrettelagt"],
  ["Frekvens", "Som regel 1 til 2 træninger om ugen"],
  ["Opfølgning", "Spørgeskemaer ved start, efter 10 og efter 20 træninger"],
];

const PRICES = [
  ["Indledende samtale", "Uforpligtende"],
  ["Enkelt træning · ca. 40 minutter", "750 kr."],
  ["Neurofeedbackforløb · 20 træninger", "15.000 kr."],
];

const Forloeb = () => (
  <main data-testid="page-forloeb">
    <PageHero
      testId="hero-forloeb"
      overline="Forløb & træning"
      title={["Et forløb,", "trin for trin."]}
      lead="Et forløb hos Center for Hjerneregulering er struktureret, individuelt tilrettelagt og gennemsigtigt. Her kan du se, hvad du kan forvente, fra første samtale til sidste evaluering."
    />

    <section className="border-b border-hairline">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <ChapterLabel number="01" label="Dit forløb" />
        </Reveal>
        <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
          <MaskedLines mode="view" lines={["Fem trin.", "Ét menneske i centrum."]} />
        </h2>
        <div className="mt-12">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.04}>
              <div className="grid grid-cols-1 gap-4 border-t border-hairline py-10 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-3">
                  <span className="font-serif text-5xl font-light tracking-tight text-sand lg:text-7xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="lg:col-span-4">
                  <h3 className="font-serif text-2xl tracking-tight text-charcoal sm:text-3xl">{s.title}</h3>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-base leading-relaxed text-graphite">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-hairline" />
        </div>
      </div>
    </section>

    <section className="border-b border-hairline bg-surface">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-7">
          <Reveal>
            <ChapterLabel number="02" label="Praktisk" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Sådan ser rammerne ud"]} />
          </h2>
          <Reveal delay={0.15} className="mt-12">
            <div className="grid grid-cols-1 gap-px border border-sand bg-sand sm:grid-cols-2">
              {FACTS.map(([t, d]) => (
                <div key={t} className="bg-canvas p-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted">{t}</p>
                  <p className="mt-3 text-lg leading-snug text-charcoal">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 max-w-2xl">
            <p className="text-sm leading-relaxed text-muted">
              Frekvens og tilrettelæggelse tilpasses altid det enkelte menneske. Planen lægges i
              fællesskab og justeres, hvis der er behov for noget andet undervejs.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <ImageFrame
              src={IMAGES.childSession}
              alt="Neurofeedbacktræning med sensorer på hovedbunden"
              caption="Fig. 01 — Træningen foregår i ro"
              testId="forloeb-space-img"
              tone="none"
            />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-b border-hairline">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-6">
          <Reveal>
            <ChapterLabel number="03" label="Hvorfor minimum 20 træninger?" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Træning tager tid.", "Det er pointen."]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p>Neurofeedback er træning, ikke en enkeltstående behandling.</p>
            <p>
              Nogle oplever ændringer relativt tidligt i forløbet, mens andre har brug for længere tid.
              Målet er ikke blot en kortvarig effekt efter den enkelte session, men at træningen over tid
              understøtter hjernens evne til regulering.
            </p>
            <p>
              Derfor arbejder Center for Hjerneregulering som udgangspunkt med forløb på minimum 20
              træninger. Herefter vurderes udviklingen individuelt, og der tages stilling til, om
              yderligere træning giver mening.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal>
            <ChapterLabel number="04" label="Din udvikling" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Jeg følger dig", "hele vejen"]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p>
              Jeg bruger spørgeskemaer systematisk til at følge din oplevelse og udvikling over tid: ved
              forløbets start, efter 10 træninger og igen efter 20 træninger. Områderne kan handle om søvn,
              fokus, indre uro, stressbelastning, følelsesmæssig regulering, energi og overstimulering.
            </p>
            <p>
              Spørgeskemaerne er ikke en diagnose eller en medicinsk vurdering. De er en struktureret måde
              at følge netop din oplevelse på, så jeg kan tilpasse træningen, og vi kan tale åbent om
              udviklingen undervejs.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section>
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <ChapterLabel number="05" label="Priser" />
        </Reveal>
        <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
          <MaskedLines mode="view" lines={["Åbne priser,", "ingen overraskelser"]} />
        </h2>
        <Reveal delay={0.15} className="mt-12 max-w-3xl">
          <div>
            {PRICES.map(([t, p]) => (
              <div
                key={t}
                className="flex flex-col gap-2 border-t border-hairline py-6 sm:flex-row sm:items-baseline sm:justify-between"
              >
                <span className="text-base text-charcoal">{t}</span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted">{p}</span>
              </div>
            ))}
            <div className="border-t border-hairline" />
          </div>
          <p className="mt-8 text-sm leading-relaxed text-muted">
            Den indledende samtale er altid uforpligtende. Behovet for træning er individuelt, og det
            vurderes sammen med dig undervejs, hvad der giver mening.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABand
      title="Tag det første skridt"
      sub="Book en indledende samtale, og få et ærligt billede af, hvad et forløb kan betyde for dig eller dit barn."
    />
  </main>
);

export default Forloeb;
